"use client";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { stripeBillingDetailsKeys, useCreateSetupIntent } from "@/hooks/useStripeBilling";
import { useOrganizations } from "@/hooks/useOrganizations";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { stripePromise } from "@/lib/stripe";
import { useCallback, useEffect, useState } from "react";
interface AddPaymentMethodModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
function PaymentForm({
  onSuccess,
  onCancel
}: {
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    try {
      const {
        error: submitError
      } = await stripe.confirmSetup({
        elements,
        redirect: "if_required"
      });
      if (submitError) {
        setError(submitError.message ?? "Failed to save payment method.");
        return;
      }
      onSuccess();
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [stripe, elements, onSuccess]);
  return <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="white" size="sm" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" variant="brand" size="sm" disabled={!stripe || submitting}>
          {submitting ? "Saving..." : "Save card"}
        </Button>
      </div>
    </form>;
}
export function AddPaymentMethodModal({
  open,
  onOpenChange
}: AddPaymentMethodModalProps) {
  const {
    selectedOrgId
  } = useOrganizations();
  const createSetupIntent = useCreateSetupIntent(selectedOrgId ?? "");
  const queryClient = useQueryClient();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !selectedOrgId) return;
    setClientSecret(null);
    setLoadError(null);
    createSetupIntent.mutateAsync().then(data => {
      setClientSecret(data.clientSecret);
    }).catch(() => {
      setLoadError("Failed to load payment form. Please try again.");
    });
    // Only re-run when modal opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedOrgId]);
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);
  const handleSuccess = useCallback(() => {
    queryClient.refetchQueries({
      queryKey: stripeBillingDetailsKeys.all
    });
    onOpenChange(false);
  }, [onOpenChange, queryClient]);
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add payment method</DialogTitle>
          <DialogDescription>
            Enter your card details below. Your card will be saved for future payments.
          </DialogDescription>
        </DialogHeader>
        {loadError ? <div className="flex h-32 flex-col items-center justify-center gap-2">
            <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
            <Button variant="white" size="sm" onClick={handleClose}>
              Close
            </Button>
          </div> : clientSecret && stripePromise ? <Elements stripe={stripePromise} options={{
        clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#18181b"
          }
        }
      }}>
            <PaymentForm onSuccess={handleSuccess} onCancel={handleClose} />
          </Elements> : <div className="flex h-32 items-center justify-center">
            <p className="text-ds-text-muted text-sm">Loading payment form...</p>
          </div>}
      </DialogContent>
    </Dialog>;
}